import { productionOverheadLines } from "@/lib/productionCostOverhead";
import React from "react";
import { Archive, Eye, Factory, Loader2, Pencil, Plus, Printer, RefreshCw, Search, Trash2, Wand2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useColorSettings } from "@/contexts/ColorSettingsContext";
import {
  archiveManufacturingSimulation,
  deleteManufacturingSimulation,
  fetchSimulationHistory,
  fetchManufacturingSimulation,
} from "@/lib/api/manufacturingSimulation";
import { formatMoney } from "@/lib/api/manufacturing";
import { buildPrintModelFromRecordAsync } from "@/lib/manufacturingSimulationPrint";
import { generateSimulationPdf, SIMULATION_REPORT_TYPES } from "@/lib/manufacturingSimulationPdf";
import { SimulationPrintDialog } from "@/components/manufacturing/SimulationPrintDialog";

import { SimulationSizeColorMatrix } from "@/components/manufacturing/SimulationSizeColorMatrix";
import { SimulationCostSummary } from "@/components/manufacturing/SimulationCostSummary";
import { SimulationSplitDialog } from "@/components/manufacturing/SimulationSplitDialog";
import { canSplitSimulation } from "@/lib/simulationSplit";
import { SIMULATION_SIZES } from "@/lib/constants";

function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("es-ES");
}

export function SimulationHistory({ reloadKey = 0, onCreate, onMagicCreate, onEdit, onConvert, convertingId = null }) {
  const [history, setHistory] = React.useState([]);
  const [splitSimulation, setSplitSimulation] = React.useState(null);
  const [selectedSimulation, setSelectedSimulation] = React.useState(null);
  const [status, setStatus] = React.useState("saved");
  const [articleSearch, setArticleSearch] = React.useState("");
  const [appliedArticleSearch, setAppliedArticleSearch] = React.useState("");
  const requestSequence = React.useRef(0);
  const [loading, setLoading] = React.useState(false);
  const [printModel, setPrintModel] = React.useState(null);
  const [printDialogOpen, setPrintDialogOpen] = React.useState(false);
  const { toast } = useToast();
  const { colorSettings } = useColorSettings();

  const [printingCompact, setPrintingCompact] = React.useState(false);
  const printSimulation = async (simulation, compact = false) => {
    if (compact) setPrintingCompact(true);
    try {
      const model = await buildPrintModelFromRecordAsync(simulation, colorSettings);
      if (!model.items.length) return toast({ title: "Nada que imprimir", description: "Esta simulación no contiene artículos.", variant: "destructive" });
      if (compact) { await generateSimulationPdf(model, { reportType: SIMULATION_REPORT_TYPES.COMPACT }); return; }
      setPrintModel(model);
      setPrintDialogOpen(true);
    } catch (error) {
      console.error("Error preparando impresión de simulación:", error);
      toast({ title: "Error", description: "No se pudo preparar la impresión.", variant: "destructive" });
    } finally { if (compact) setPrintingCompact(false); }
  };

  const loadHistory = React.useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    const result = await fetchSimulationHistory({ status, limit: 100, articleSearch: appliedArticleSearch });
    if (request !== requestSequence.current) return;
    if (result.error) toast({ title: "Error", description: "No se pudo cargar el grid de simulaciones", variant: "destructive" });
    setHistory(result.data || []);
    setSelectedSimulation((current) => (result.data || []).find((row) => row.id === current?.id) || null);
    setLoading(false);
  }, [status, appliedArticleSearch, toast]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setAppliedArticleSearch(articleSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [articleSearch]);

  React.useEffect(() => {
    loadHistory();
    return () => { requestSequence.current += 1; };
  }, [loadHistory, reloadKey]);

  const archiveSimulation = async (simulation) => {
    if (!window.confirm(`¿Archivar la simulación "${simulation.description}"?`)) return;
    const result = await archiveManufacturingSimulation(simulation.id, simulation.content_version);
    if (result.error) return toast({ title: "Error", description: result.error.message, variant: "destructive" });
    await loadHistory();
  };

  const deleteSimulation = async (simulation) => {
    if (!window.confirm(`¿Eliminar definitivamente la simulación "${simulation.description}"?`)) return;
    const result = await deleteManufacturingSimulation(simulation.id, simulation.content_version);
    if (result.error) return toast({ title: "Error", description: result.error.message, variant: "destructive" });
    await loadHistory();
  };

  const openSplitDestination = async (id) => {
    const response = await fetchManufacturingSimulation(id);
    if (response.error) return toast({ title: "Error", description: response.error.message, variant: "destructive" });
    setSplitSimulation(null);
    onEdit?.(response.data);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Simulaciones pedidos/presupuestos de fabricación</h2>
            <p className="text-sm text-gray-600">Alta, consulta, modificación, archivado y borrado de estudios de costes.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select aria-label="Estado de las simulaciones" className="rounded-md border bg-white px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="saved">Activas</option>
              <option value="archived">Archivadas</option>
              <option value="all">Todas</option>
            </select>
            <Button type="button" variant="outline" onClick={loadHistory} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />Recargar
            </Button>
            {onMagicCreate && (
              <Button type="button" variant="secondary" onClick={onMagicCreate}>
                <Wand2 className="mr-2 h-4 w-4" />Crear mágico
              </Button>
            )}
            <Button type="button" onClick={onCreate}>
              <Plus className="mr-2 h-4 w-4" />Nueva simulación
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full sm:max-w-md">
            <label htmlFor="simulation-article-search" className="mb-1 block text-sm font-medium">Filtrar por artículo</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input id="simulation-article-search" type="search" placeholder="Código o descripción del artículo" className="pl-9 pr-10" value={articleSearch} onChange={(event) => { setArticleSearch(event.target.value); setSelectedSimulation(null); }} />
              {articleSearch && <Button type="button" variant="ghost" size="icon" className="absolute right-1 top-1 h-8 w-8" aria-label="Limpiar filtro de artículo" onClick={() => { setArticleSearch(""); setSelectedSimulation(null); }}><X className="h-4 w-4" /></Button>}
            </div>
          </div>
          <p className="text-sm text-gray-500" role="status">{loading || articleSearch.trim() !== appliedArticleSearch ? "Buscando simulaciones..." : `${history.length} simulaciones${history.length === 100 ? " (últimas 100 coincidencias)" : ""}`}. Pulsa una fila para editar.</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="border px-3 py-2">Fecha</th>
                <th className="border px-3 py-2">Descripción</th>
                <th className="border px-3 py-2 text-right">Artículos</th>
                <th className="border px-3 py-2 text-right">Prendas</th>
                <th className="border px-3 py-2 text-right">Coste total</th>
                <th className="border px-3 py-2">Estado</th>
                <th className="border px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {history.map((simulation) => (
                <tr key={simulation.id}
                  className={`${simulation.production_orders?.length ? "bg-green-100 hover:bg-green-200" : selectedSimulation?.id === simulation.id ? "bg-blue-50 hover:bg-blue-50" : "bg-white hover:bg-blue-50"} ${onEdit ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500" : ""}`}
                  tabIndex={onEdit ? 0 : undefined}
                  aria-label={onEdit ? `Editar simulación: ${simulation.description}` : undefined}
                  onClick={() => { if (simulation.status === "saved") onEdit?.(simulation); else setSelectedSimulation(simulation); }}
                  onKeyDown={(event) => {
                    if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      if (simulation.status === "saved") onEdit?.(simulation); else setSelectedSimulation(simulation);
                    }
                  }}
                >
                  <td className="border px-3 py-2">{formatDate(simulation.simulation_date)}</td>
                  <td className="border px-3 py-2 font-medium">{simulation.description}{simulation.parent_simulation_id && <span className="block text-xs font-normal text-gray-500">Dividida de: {simulation.origin?.description || history.find(row => row.id === simulation.parent_simulation_id)?.description || simulation.parent_simulation_id}</span>}{!!simulation.splits?.length && <span className="block text-xs font-normal text-gray-500">Con simulaciones derivadas</span>}</td>
                  <td className="border px-3 py-2 text-right">{simulation.items?.length || 0}</td>
                  <td className="border px-3 py-2 text-right">{simulation.total_quantity}</td>
                  <td className="border px-3 py-2 text-right">{formatMoney(simulation.grand_total)}</td>
                  <td className="border px-3 py-2">{simulation.status === "archived" ? "Archivada" : "Activa"}{simulation.production_orders?.length > 0 && <span className="block text-xs font-medium text-green-800">Orden creada</span>}</td>
                  <td className="border px-3 py-2" onClick={(event) => event.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="outline" size="sm" title="Consultar" onClick={() => setSelectedSimulation(simulation)}><Eye className="h-4 w-4" /></Button>
                      <Button type="button" variant="outline" size="sm" disabled={printingCompact} onClick={() => printSimulation(simulation, true)}>Imprimir compacto</Button>
                      <Button type="button" variant="outline" size="sm" title="Imprimir" onClick={() => printSimulation(simulation)}><Printer className="h-4 w-4" /></Button>
                      <Button type="button" variant="outline" size="sm" title="Editar" disabled={simulation.status !== "saved"} onClick={() => onEdit?.(simulation)}><Pencil className="h-4 w-4" /></Button>
                      {canSplitSimulation(simulation) && <Button type="button" variant="outline" size="sm" onClick={() => setSplitSimulation(simulation)}>Dividir</Button>}
                      {onConvert && simulation.status !== "archived" && (
                        <Button type="button" variant="outline" size="sm" title="Convertir en orden de fabricación" disabled={convertingId === simulation.id || !!simulation.production_orders?.length} onClick={() => onConvert(simulation)}>
                          {convertingId === simulation.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Factory className="h-4 w-4" />}
                        </Button>
                      )}
                      {simulation.status !== "archived" && <Button type="button" variant="outline" size="sm" title="Archivar" onClick={() => archiveSimulation(simulation)}><Archive className="h-4 w-4" /></Button>}
                      <Button type="button" variant="outline" size="sm" title="Eliminar" onClick={() => deleteSimulation(simulation)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!history.length && <tr><td className="border px-3 py-6 text-center text-gray-500" colSpan={7}>{loading ? "Cargando..." : appliedArticleSearch ? "No hay simulaciones con ese artículo." : "No hay simulaciones."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSimulation && (
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">{selectedSimulation.description}</h3>
              <p className="text-sm text-gray-600">{selectedSimulation.notes || "Sin observaciones"}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" disabled={selectedSimulation.status !== "saved"} onClick={() => onEdit?.(selectedSimulation)}><Pencil className="mr-2 h-4 w-4" />Editar</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => printSimulation(selectedSimulation)}><Printer className="mr-2 h-4 w-4" />Imprimir</Button>
              <Button type="button" variant="outline" size="sm" disabled={printingCompact} onClick={() => printSimulation(selectedSimulation, true)}><Printer className="mr-2 h-4 w-4" />Imprimir compacto</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setSelectedSimulation(null)}><X className="mr-2 h-4 w-4" />Cerrar consulta</Button>
            </div>
          </div>
          <div className="space-y-3">
            {(selectedSimulation.items || []).map((item) => {
              const lines = [...(item.color_lines || [])].sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
              const product = item.product || {};
              const image = product.images?.[product.main_image_index || 0] || product.images?.[0];
              const quantities = Object.fromEntries(lines.flatMap((line) => SIMULATION_SIZES.map((size) => [`${line.color}::${size}`, Number(line[`quantity_${size.toLowerCase()}`] || 0)])));
              const contrasts = Object.fromEntries(lines.map((line) => [line.color, line]));
              return <div key={item.id} className="overflow-hidden rounded-lg border-2 border-blue-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center gap-3 border-b bg-blue-50/40 p-3">
                  {image ? <img src={image} alt={item.description_snapshot} className="h-24 w-24 flex-shrink-0 rounded bg-white object-contain" /> : <div className="flex h-24 w-24 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">Sin imagen</div>}
                  <div className="min-w-0 flex-1"><h3 className="font-semibold">{item.product_code_snapshot} · {item.description_snapshot}</h3><p className="mt-1 text-sm text-gray-600">{item.season_snapshot || product.temporada || "Sin temporada"} · {product.composicion || "Sin composición"}</p></div>
                  <div className="text-right text-sm"><p>{item.total_quantity} prendas</p><b className="text-blue-900">{formatMoney(item.total_cost)}</b></div>
                </div>
                <div className="space-y-3 p-4">
                  <SimulationSizeColorMatrix readOnly colors={lines.map((line) => line.color)} quantities={quantities} contrasts={contrasts} />
                  {(item.manufacturer_reference || item.pattern_code || item.notes) && <p className="text-sm text-gray-600">{[item.manufacturer_reference && `Ref. fabricante: ${item.manufacturer_reference}`, item.pattern_code && `Patrón: ${item.pattern_code}`, item.notes].filter(Boolean).join(" · ")}</p>}
                </div>
              </div>;
            })}
            <SimulationCostSummary totals={{ ...selectedSimulation, overhead_lines: productionOverheadLines(selectedSimulation.items) }} productCount={selectedSimulation.items?.length || 0} itemCount={(selectedSimulation.items || []).reduce((sum, item) => sum + (item.color_lines?.length || 0), 0)} />
          </div>
        </div>
      )}

      {splitSimulation && <SimulationSplitDialog key={splitSimulation.id} simulation={splitSimulation} onClose={() => setSplitSimulation(null)} onCompleted={() => { void loadHistory(); }} onOpenDestination={openSplitDestination} />}
      <SimulationPrintDialog open={printDialogOpen} onOpenChange={setPrintDialogOpen} model={printModel} />
    </div>
  );
}
